import { type FC, type SubmitEvent, useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Radio,
  Stack,
  Typography,
} from '@mui/material';

import { type CampaignsFiltersSelection } from '@/components/CampaignsFilters';
import { useIsMobile } from '@/hooks/useBreakpoints';
import { useExchangesContext } from '@/providers/ExchangesProvider';
import { config as wagmiConfig } from '@/providers/WagmiProvider';
import { CampaignStatus, CampaignType } from '@/types';
import { mapTypeToLabel } from '@/utils';

const controlSlotProps = {
  root: {
    sx: {
      ml: 1.5,
      py: 0,
      px: 0,
    },
  },
};

const labelSlotProps = {
  typography: {
    sx: {
      color: 'neutral.100',
      textTransform: 'capitalize',
      ml: 1,
    },
  },
};

const CheckboxIcon = () => (
  <Box
    sx={{
      width: 20,
      height: 20,
      bgcolor: 'transparent',
      borderRadius: '4px',
      border: '1.5px solid',
      borderColor: 'border.strong',
    }}
  />
);

const CheckboxCheckedIcon = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 20,
      height: 20,
      borderRadius: '4px',
      bgcolor: 'accent.main',
    }}
  >
    <CheckIcon sx={{ color: 'neutral.100', fontSize: 16 }} />
  </Box>
);

const statusOptions = [...Object.values(CampaignStatus)]
  .filter((status) => status !== CampaignStatus.TO_CANCEL)
  .map((status) => ({
    value: status,
    label: status,
  }));

const campaignTypeOptions = [...Object.values(CampaignType)].map(
  (campaignType) => ({
    value: campaignType,
    label: mapTypeToLabel(campaignType),
  })
);

const networkOptions = wagmiConfig.chains.map((chain) => ({
  value: chain.id,
  label: chain.name,
}));

const ALL_OPTION_VALUE = 'all' as const;

type Props = {
  appliedFilters: CampaignsFiltersSelection;
  onApplyFilters: (filters: CampaignsFiltersSelection) => void;
};

type MultiSelectSection = 'campaignTypes' | 'exchanges' | 'statuses';

const CampaignsFiltersContent: FC<Props> = ({
  appliedFilters,
  onApplyFilters,
}) => {
  const [draftFilters, setDraftFilters] =
    useState<CampaignsFiltersSelection>(appliedFilters);

  const { exchanges = [] } = useExchangesContext();
  const isMobile = useIsMobile();

  const exchangeOptions = exchanges.map(({ name, display_name }) => ({
    value: name,
    label: display_name,
  }));
  const campaignTypeValues = campaignTypeOptions.map((option) => option.value);
  const exchangeValues = exchangeOptions.map((option) => option.value);
  const statusValues = statusOptions.map((option) => option.value);
  const sectionOptions: Record<MultiSelectSection, string[]> = {
    campaignTypes: campaignTypeValues,
    exchanges: exchangeValues,
    statuses: statusValues,
  };

  const isAllCampaignTypesSelected =
    draftFilters.campaignTypes.includes(ALL_OPTION_VALUE);
  const isAllExchangesSelected =
    draftFilters.exchanges.includes(ALL_OPTION_VALUE);
  const isAllStatusesSelected =
    draftFilters.statuses.includes(ALL_OPTION_VALUE);

  const handleToggleAll = (section: MultiSelectSection) => {
    const currentValues = draftFilters[section] as string[];
    const isAllSelected = currentValues.includes(ALL_OPTION_VALUE);

    setDraftFilters((previous) => ({
      ...previous,
      [section]: isAllSelected ? [] : [ALL_OPTION_VALUE],
    }));
  };

  const handleToggleOption = (value: string, section: MultiSelectSection) => {
    const options = sectionOptions[section];
    const currentValues = draftFilters[section] as string[];
    let nextValues: string[];

    if (currentValues.includes(ALL_OPTION_VALUE)) {
      nextValues = options.filter((option) => option !== value);
    } else if (currentValues.includes(value)) {
      nextValues = currentValues.filter(
        (selectedValue) => selectedValue !== value
      );
    } else {
      nextValues = [...currentValues, value];
    }

    setDraftFilters((previous) => ({
      ...previous,
      [section]:
        nextValues.length === options.length ? [ALL_OPTION_VALUE] : nextValues,
    }));
  };

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    onApplyFilters(draftFilters);
  };

  const handleClearAll = () => {
    setDraftFilters({
      network: appliedFilters.network,
      statuses: [],
      campaignTypes: [],
      exchanges: [],
    });
  };

  const disableClearAll = Object.entries(draftFilters)
    .filter(([key]) => key !== 'network')
    .every(([, value]) => (Array.isArray(value) ? value.length === 0 : !value));

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit}
      sx={{
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Typography
        variant={isMobile ? 'body4' : 'h4'}
        sx={{
          color: 'neutral.100',
          ml: { xs: 2, md: 4 },
          mb: 3,
        }}
      >
        Campaign Filters
      </Typography>
      <Stack
        sx={{
          pt: 2,
          pb: 3,
          gap: 2,
          minHeight: 0,
          flex: 1,
          overflow: 'auto',
        }}
      >
        <Stack
          sx={{
            gap: 1,
            px: { xs: 2, md: 4 },
          }}
        >
          <Typography variant="body1" sx={{ textTransform: 'uppercase' }}>
            Network
          </Typography>
          {networkOptions.map(({ label, value }) => (
            <FormControlLabel
              key={value}
              label={label}
              slotProps={labelSlotProps}
              control={
                <Radio
                  checked={draftFilters.network === value}
                  slotProps={controlSlotProps}
                  icon={
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: 'transparent',
                        borderRadius: '50%',
                        border: '1.5px solid',
                        borderColor: 'border.strong',
                      }}
                    />
                  }
                  checkedIcon={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: 'accent.main',
                      }}
                    >
                      <CheckIcon sx={{ color: 'neutral.100', fontSize: 16 }} />
                    </Box>
                  }
                />
              }
              onChange={() =>
                setDraftFilters((previous) => ({
                  ...previous,
                  network: value,
                }))
              }
            />
          ))}
        </Stack>
        <Divider sx={{ borderColor: 'border.strong' }} />
        <Stack
          sx={{
            gap: 1,
            px: { xs: 2, md: 4 },
          }}
        >
          <Typography variant="body1" sx={{ textTransform: 'uppercase' }}>
            Status
          </Typography>
          <FormControlLabel
            label="All"
            control={
              <Checkbox
                checked={isAllStatusesSelected}
                slotProps={controlSlotProps}
                icon={<CheckboxIcon />}
                checkedIcon={<CheckboxCheckedIcon />}
              />
            }
            slotProps={labelSlotProps}
            onChange={() => handleToggleAll('statuses')}
          />
          {statusOptions.map(({ label, value }) => (
            <FormControlLabel
              key={value}
              label={label}
              control={
                <Checkbox
                  checked={
                    isAllStatusesSelected ||
                    draftFilters.statuses.includes(value)
                  }
                  slotProps={controlSlotProps}
                  icon={<CheckboxIcon />}
                  checkedIcon={<CheckboxCheckedIcon />}
                />
              }
              slotProps={labelSlotProps}
              onChange={() => handleToggleOption(value, 'statuses')}
            />
          ))}
        </Stack>
        <Divider sx={{ borderColor: 'border.strong' }} />
        <Stack
          sx={{
            gap: 1,
            px: { xs: 2, md: 4 },
          }}
        >
          <Typography variant="body1" sx={{ textTransform: 'uppercase' }}>
            Campaign Type
          </Typography>
          <FormControlLabel
            label="All"
            control={
              <Checkbox
                checked={isAllCampaignTypesSelected}
                slotProps={controlSlotProps}
                icon={<CheckboxIcon />}
                checkedIcon={<CheckboxCheckedIcon />}
              />
            }
            slotProps={labelSlotProps}
            onChange={() => handleToggleAll('campaignTypes')}
          />
          {campaignTypeOptions.map(({ label, value }) => (
            <FormControlLabel
              key={value}
              label={label}
              control={
                <Checkbox
                  checked={
                    isAllCampaignTypesSelected ||
                    draftFilters.campaignTypes.includes(value)
                  }
                  slotProps={controlSlotProps}
                  icon={<CheckboxIcon />}
                  checkedIcon={<CheckboxCheckedIcon />}
                />
              }
              slotProps={labelSlotProps}
              onChange={() => handleToggleOption(value, 'campaignTypes')}
            />
          ))}
        </Stack>
        <Divider sx={{ borderColor: 'border.strong' }} />
        <Stack
          sx={{
            gap: 1,
            px: { xs: 2, md: 4 },
          }}
        >
          <Typography variant="body1" sx={{ textTransform: 'uppercase' }}>
            Exchanges
          </Typography>
          <FormControlLabel
            label="All"
            control={
              <Checkbox
                checked={isAllExchangesSelected}
                slotProps={controlSlotProps}
                icon={<CheckboxIcon />}
                checkedIcon={<CheckboxCheckedIcon />}
              />
            }
            slotProps={labelSlotProps}
            onChange={() => handleToggleAll('exchanges')}
          />
          {exchangeOptions.map(({ label, value }) => (
            <FormControlLabel
              key={value}
              label={label}
              control={
                <Checkbox
                  checked={
                    isAllExchangesSelected ||
                    draftFilters.exchanges.includes(value)
                  }
                  slotProps={controlSlotProps}
                  icon={<CheckboxIcon />}
                  checkedIcon={<CheckboxCheckedIcon />}
                />
              }
              slotProps={labelSlotProps}
              onChange={() => handleToggleOption(value, 'exchanges')}
            />
          ))}
        </Stack>
      </Stack>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: '1px solid',
          borderColor: 'border.strong',
          py: 3,
          px: { xs: 2, md: 3 },
          gap: 2,
        }}
      >
        <Button
          size="large"
          variant="outlined"
          disabled={disableClearAll}
          fullWidth={isMobile}
          disableRipple
          sx={{
            color: 'neutral.100',
          }}
          onClick={handleClearAll}
        >
          Clear All
        </Button>
        <Button
          type="submit"
          size="large"
          variant="contained"
          color="accent"
          fullWidth={isMobile}
          disableRipple
        >
          Apply filters
        </Button>
      </Box>
    </Stack>
  );
};

export default CampaignsFiltersContent;
